import React, { useContext } from 'react'

import { SLAClockContext } from '../../components/SLAChip'
import { formatWhen, relativeWhen } from '../../helpers/ticketWorkflow'

/**
 * A moment in time, said the way a reader wants it and kept the way an auditor
 * needs it.
 *
 * Relative in the text, absolute in the tooltip, machine-readable in
 * `dateTime`. All three matter and they are not interchangeable: the reader
 * triaging wants "2 hours ago", the reader disputing an SLA wants the second,
 * and `<time dateTime>` is what makes the value parseable by anything that
 * walks the DOM.
 *
 * The `<time>` element specifically, rather than a span with a title —
 * TicketActivity already used it and was the only place that did. This is that
 * pattern, moved somewhere the rest of the screen can reach.
 *
 * Reads the shared SLA clock so every relative string on the page is computed
 * against ONE instant. Eight independent `new Date()` calls during a render
 * can straddle a minute boundary, and two timestamps a second apart would then
 * disagree about how long ago they were.
 */
export default function When({
  iso,
  absolute = false
}: {
  iso: string
  /**
   * Show the exact moment inline, after the relative one: `last week
   * (8/29/2026, 12:00:00 PM)`.
   *
   * OPT-IN rather than always on, because the two readings serve different
   * screens. A rail that says "opened last week" is orienting you; a
   * conversation or an audit trail is being read for sequence — which comment
   * came before which state change — and "last week · last week · last week"
   * orders nothing. There the exact time is the content, not a footnote.
   *
   * It stays in the tooltip too. The tooltip is the only form available to the
   * call sites that do NOT opt in, and removing it there to avoid duplication
   * here would take precision away from four places to tidy one.
   */
  absolute?: boolean
}) {
  const clock = useContext(SLAClockContext)
  const at = clock ?? new Date()
  if (!iso) return <>{'—'}</>
  // Computed once. The tooltip always carries the exact time and `absolute`
  // renders the same string inline, so calling formatWhen twice built two
  // Intl.DateTimeFormat instances per row — 300 of them on a full thread plus
  // activity feed, half of them re-deriving a value from the line above.
  const exact = formatWhen(iso)
  return (
    <time dateTime={iso} title={exact}>
      {relativeWhen(iso, at)}
      {absolute && (
        <>
          {' '}
          {/* The parenthesised absolute is isolated as its own bdi: under /fa
              the relative half is Persian and the formatted date carries Latin
              digits and separators, so without isolation the closing bracket
              lands on the wrong side of the number. */}
          <bdi>({exact})</bdi>
        </>
      )}
    </time>
  )
}
